import { useCallback, useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { apiFetch } from "../lib/api";
import { displayName } from "../lib/profile";

// Profile view. Focus of M7: the "Conectar Claude (MCP)" section — shows the
// one-line MCP server URL + how to connect, the current connection status, and a
// revoke-access action. The account fields mirror the mockup's Perfil view.

interface GrantsResp {
  ok: boolean;
  connected: boolean;
  count: number;
  grants: { id: string; client: string; created_at: string }[];
}

const T = {
  es: {
    title: "Perfil",
    acct: "Cuenta",
    fName: "Nombre",
    fEmail: "Email (Google)",
    mcpT: "Conectar Claude (MCP)",
    mcpLead:
      "Deja que Claude (u otro cliente MCP) lea tus sitios y recomendaciones semanales directamente. Solo lectura — inicias sesión con Google y autorizas en el navegador; no se manejan tokens manuales.",
    urlLabel: "URL del servidor MCP",
    copy: "Copiar",
    copied: "Copiado",
    steps: "Cómo conectar",
    s1: "En Claude, abre Configuración → Conectores → Agregar servidor MCP personalizado.",
    s2: "Pega la URL del servidor de arriba.",
    s3: "Se abrirá el navegador: inicia sesión con Google y autoriza. Claude queda conectado.",
    statusOn: "Conectado",
    statusOff: "Sin conexiones activas",
    connectedVia: "Autorizado a",
    since: "desde",
    revoke: "Revocar acceso",
    revoking: "Revocando…",
    revoked: "Acceso revocado.",
    loadErr: "No se pudo cargar el estado de conexión.",
    revokeErr: "No se pudo revocar. Intenta de nuevo.",
  },
  en: {
    title: "Profile",
    acct: "Account",
    fName: "Name",
    fEmail: "Email (Google)",
    mcpT: "Connect Claude (MCP)",
    mcpLead:
      "Let Claude (or another MCP client) read your sites and weekly recommendations directly. Read-only — you sign in with Google and authorize in the browser; no manual tokens.",
    urlLabel: "MCP server URL",
    copy: "Copy",
    copied: "Copied",
    steps: "How to connect",
    s1: "In Claude, open Settings → Connectors → Add custom MCP server.",
    s2: "Paste the server URL above.",
    s3: "The browser opens: sign in with Google and authorize. Claude is now connected.",
    statusOn: "Connected",
    statusOff: "No active connections",
    connectedVia: "Authorized to",
    since: "since",
    revoke: "Revoke access",
    revoking: "Revoking…",
    revoked: "Access revoked.",
    loadErr: "Could not load connection status.",
    revokeErr: "Could not revoke. Please try again.",
  },
};

export default function Profile({
  lang,
  session,
}: {
  lang: "es" | "en";
  session: Session;
}) {
  const t = T[lang];
  const token = session.access_token;
  const serverUrl = `${window.location.origin}/mcp`;

  const [grants, setGrants] = useState<GrantsResp | null>(null);
  const [loadErr, setLoadErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoadErr(false);
    try {
      const data = await apiFetch<GrantsResp>("/api/mcp/grants", token);
      setGrants(data);
    } catch {
      setLoadErr(true);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function copyUrl() {
    try {
      await navigator.clipboard.writeText(serverUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard may be blocked; the URL is visible to select manually */
    }
  }

  async function revoke() {
    setBusy(true);
    setMsg(null);
    try {
      await apiFetch("/api/mcp/grants", token, { method: "DELETE" });
      setMsg(t.revoked);
      await load();
    } catch {
      setMsg(t.revokeErr);
    } finally {
      setBusy(false);
    }
  }

  const name = displayName(session.user) ?? session.user.email ?? "";

  return (
    <>
      <div className="h-row">
        <h1>{t.title}</h1>
      </div>
      <p className="sub-line" />

      <div className="card mb">
        <div className="card-h">
          <h3>{t.acct}</h3>
        </div>
        <div style={{ padding: "0 16px 16px" }}>
          <div className="fld">
            <label>{t.fName}</label>
            <input className="inp" value={name} disabled />
          </div>
          <div className="fld">
            <label>{t.fEmail}</label>
            <input className="inp" value={session.user.email ?? ""} disabled />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-h">
          <h3>{t.mcpT}</h3>
          {grants?.connected && (
            <span
              className="badge"
              style={{ marginLeft: "auto", background: "var(--brand)", color: "#fff" }}
            >
              {t.statusOn}
            </span>
          )}
        </div>
        <div style={{ padding: "0 16px 18px" }}>
          <p className="sub-line" style={{ margin: "0 0 14px" }}>
            {t.mcpLead}
          </p>

          <div className="fld">
            <label>{t.urlLabel}</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                className="inp"
                value={serverUrl}
                readOnly
                onFocus={(e) => e.currentTarget.select()}
                style={{ fontFamily: "var(--mono)", fontSize: ".82rem" }}
              />
              <button
                className="btn btn-ghost"
                onClick={copyUrl}
                style={{ whiteSpace: "nowrap" }}
              >
                {copied ? t.copied : t.copy}
              </button>
            </div>
          </div>

          <div className="fld">
            <label>{t.steps}</label>
            <ol
              style={{
                margin: "4px 0 0",
                paddingLeft: 18,
                color: "var(--muted)",
                fontSize: ".84rem",
                lineHeight: 1.6,
              }}
            >
              <li>{t.s1}</li>
              <li>{t.s2}</li>
              <li>{t.s3}</li>
            </ol>
          </div>

          {loadErr && <p className="auth-err">{t.loadErr}</p>}

          {grants && (
            <div style={{ marginTop: 16 }}>
              {grants.connected ? (
                <>
                  {grants.grants.map((g) => (
                    <p
                      key={g.id}
                      className="sub-line"
                      style={{ margin: "0 0 8px" }}
                    >
                      {t.connectedVia} <b>{g.client}</b> {t.since}{" "}
                      {new Date(g.created_at).toLocaleDateString(
                        lang === "es" ? "es-MX" : "en-US",
                      )}
                    </p>
                  ))}
                  <button
                    className="btn btn-ghost"
                    onClick={revoke}
                    disabled={busy}
                  >
                    {busy ? t.revoking : t.revoke}
                  </button>
                </>
              ) : (
                <p className="sub-line" style={{ margin: 0 }}>
                  {t.statusOff}
                </p>
              )}
              {msg && (
                <p className="sub-line" style={{ margin: "8px 0 0" }}>
                  {msg}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
